/* sample-cart.logic.js
 * Impulse-safe Samples ("Muster") logic
 * - Adds via Shopify /cart/add.js
 * - Triggers Impulse drawer via ajaxProduct:added
 * - Free ≤ 5, Paid > 5 (counts all sample items in cart)
 * - Proxy button support (calculator header button)
 * - Rebalance on remove/updates: ensure correct free/paid distribution
 *
 * FIX: Removed deprecated AttributionReporting API.
 *      Removed global window.fetch wrapper.
 *      Consolidated all event listeners into boot() — no duplicates.
 */

(function () {
  'use strict';

  const FREE_LIMIT = 5;
  const MAP_STORAGE_KEY = 'reud_sample_variant_map_v1';

  let rebalanceLock = false;
  let rebalanceQueued = false;
  let rebalanceTimer = null;

  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch (_) { return null; }
  }

  function loadVariantMap() {
    const raw = localStorage.getItem(MAP_STORAGE_KEY);
    const parsed = raw ? safeJsonParse(raw) : null;
    return (parsed && typeof parsed === 'object') ? parsed : {};
  }

  function saveVariantMap(map) {
    try { localStorage.setItem(MAP_STORAGE_KEY, JSON.stringify(map || {})); } catch (_) {}
  }

  function upsertVariantMap(handle, ids) {
    const h = String(handle || '').trim();
    if (!h || !ids?.freeId || !ids?.feeId) return;
    const map = loadVariantMap();
    map[h] = { freeId: Number(ids.freeId), feeId: Number(ids.feeId) };
    saveVariantMap(map);
  }

  function getIdsFromButton(btn) {
    const freeId = Number(btn.dataset.sampleFreeVariantId);
    const feeId  = Number(btn.dataset.sampleFeeVariantId);
    if (!freeId || !feeId) return null;
    return { freeId, feeId };
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
    return r.json();
  }

  function isSampleItem(it) {
    if (!it) return false;
    if (it?.properties?._is_sample === '1') return true;
    if (it?.properties?._sample_for_handle) return true;
    return false;
  }

  function countAllSamples(cart) {
    if (!cart?.items?.length) return 0;
    return cart.items.reduce((sum, it) => sum + (isSampleItem(it) ? (Number(it.quantity) || 0) : 0), 0);
  }

  function setButtonLabel(btn, totalCount) {
    const freeLabel = btn.dataset.freeLabel || 'Kostenloses Muster bestellen';
    const paidLabel = btn.dataset.paidLabel || 'Muster bestellen';
    const textEl = btn.querySelector('[data-sample-button-text]') || btn;
    textEl.textContent = totalCount < FREE_LIMIT ? freeLabel : paidLabel;
  }

  function hasSampleForProduct(cart, productHandle) {
    if (!cart?.items?.length || !productHandle) return false;
    return cart.items.some(it => isSampleItem(it) && it?.properties?._sample_for_handle === productHandle);
  }

  function setDisabledState(btn, isDisabled) {
    const textEl = btn.querySelector('[data-sample-button-text]') || btn;
    if (isDisabled) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('is-disabled');
      textEl.textContent = btn.dataset.inCartLabel || 'Muster bereits im Warenkorb';
    } else {
      btn.removeAttribute('disabled');
      btn.classList.remove('is-disabled');
    }
  }

  function updateSampleButtonState(btn, cart, totalSampleCount) {
    const handle = btn.dataset.productHandle || '';
    if (handle && hasSampleForProduct(cart, handle)) {
      setDisabledState(btn, true);
      return;
    }
    setDisabledState(btn, false);
    setButtonLabel(btn, totalSampleCount);
  }

  async function addToCart(variantId, quantity = 1, properties = {}) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!r.ok) {
      let payload = null;
      try { payload = await r.json(); } catch (e) {}
      const msg = payload?.description || payload?.message || payload?.errors || ('Add failed: ' + r.status);
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    return r.json();
  }

  async function changeLineByKey(lineKey, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: Number(quantity) || 0 })
    });
    if (!r.ok) {
      const txt = await r.text().catch(() => '');
      throw new Error(`Change failed: HTTP ${r.status} ${txt}`);
    }
    return r.json();
  }

  function notifyThemeCartUpdated() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
  }

  // ---------------- PROXY BUTTON WIRING ----------------
  function wireCalculatorProxyButtons() {
    const fcRoot = document.querySelector('#floor-calculator[data-fc="root"]');
    if (!fcRoot) return;

    const themeSampleBtn =
      document.querySelector('.product-single__meta [data-sample-button]:not([data-sample-proxy])') ||
      document.querySelector('[data-sample-button]:not([data-sample-proxy])');

    const proxyBtns = fcRoot.querySelectorAll('[data-sample-button][data-sample-proxy="1"]');
    if (!proxyBtns.length) return;

    if (themeSampleBtn) {
      proxyBtns.forEach((proxy) => {
        if (!proxy.dataset.sampleFreeVariantId && themeSampleBtn.dataset.sampleFreeVariantId)
          proxy.dataset.sampleFreeVariantId = themeSampleBtn.dataset.sampleFreeVariantId;
        if (!proxy.dataset.sampleFeeVariantId && themeSampleBtn.dataset.sampleFeeVariantId)
          proxy.dataset.sampleFeeVariantId = themeSampleBtn.dataset.sampleFeeVariantId;
        if (!proxy.dataset.productHandle && themeSampleBtn.dataset.productHandle)
          proxy.dataset.productHandle = themeSampleBtn.dataset.productHandle;
        if (!proxy.dataset.productTitle && themeSampleBtn.dataset.productTitle)
          proxy.dataset.productTitle = themeSampleBtn.dataset.productTitle;
        if (!proxy.dataset.productImage && themeSampleBtn.dataset.productImage)
          proxy.dataset.productImage = themeSampleBtn.dataset.productImage;
        if (!proxy.dataset.productUrl && themeSampleBtn.dataset.productUrl)
          proxy.dataset.productUrl = themeSampleBtn.dataset.productUrl;
      });
      try {
        themeSampleBtn.style.display = 'none';
        themeSampleBtn.setAttribute('aria-hidden', 'true');
        themeSampleBtn.setAttribute('tabindex', '-1');
      } catch (_) {}
    }
  }

  // ---------------- REBALANCE LOGIC ----------------
  function queueRebalance() {
    if (rebalanceLock) {
      rebalanceQueued = true;
      return;
    }
    if (rebalanceTimer) window.clearTimeout(rebalanceTimer);
    rebalanceTimer = window.setTimeout(async () => {
      rebalanceTimer = null;
      await rebalanceSamplesIfNeeded();
    }, 120);
  }

  // ---------------- L1e — CHECKOUT GUARD HELPERS ----------------
  // Consumed by the click interceptor registered in boot(). Kept as
  // top-level helpers so they read the same rebalance state variables
  // as queueRebalance / rebalanceSamplesIfNeeded above.
  function isSampleRebalancePending() {
    return rebalanceLock || rebalanceQueued || rebalanceTimer !== null;
  }
  async function waitForSampleRebalance(maxWaitMs) {
    const cap = Number.isFinite(maxWaitMs) ? maxWaitMs : 5000;
    const start = Date.now();
    while (isSampleRebalancePending() && (Date.now() - start) < cap) {
      await new Promise(r => setTimeout(r, 50));
    }
  }

  function getIdsForSampleLine(it, map) {
    const handle = String(it?.properties?._sample_for_handle || '').trim();
    if (!handle) return null;
    const pFree = Number(it?.properties?._sample_free_variant_id || 0);
    const pFee  = Number(it?.properties?._sample_fee_variant_id || 0);
    if (pFree && pFee) return { handle, freeId: pFree, feeId: pFee };
    const ids = map?.[handle];
    if (ids?.freeId && ids?.feeId) return { handle, freeId: Number(ids.freeId), feeId: Number(ids.feeId) };
    return null;
  }

  async function rebalanceSamplesIfNeeded() {
    if (rebalanceLock) return;
    rebalanceLock = true;

    try {
      const cart = await fetchCart();
      const totalSamples = countAllSamples(cart);
      const desiredPaid = Math.max(0, totalSamples - FREE_LIMIT);
      const map = loadVariantMap();

      const lines = (cart.items || [])
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => isSampleItem(it))
        .map(({ it, idx }) => {
          const ids = getIdsForSampleLine(it, map);
          if (!ids?.handle || !ids?.freeId || !ids?.feeId) return null;
          upsertVariantMap(ids.handle, { freeId: ids.freeId, feeId: ids.feeId });
          const vid = Number(it.variant_id || 0);
          const qty = Number(it.quantity || 0) || 0;
          return {
            key: it.key,
            handle: ids.handle,
            variantId: vid,
            qty,
            idx,
            freeId: Number(ids.freeId),
            feeId: Number(ids.feeId),
            isPaidLine: vid === Number(ids.feeId),
            isFreeLine: vid === Number(ids.freeId),
            props: it.properties || {}
          };
        })
        .filter(Boolean);

      if (!lines.length) return;

      const paidQtyCurrent = lines.reduce((sum, l) => sum + (l.isPaidLine ? l.qty : 0), 0);
      if (paidQtyCurrent === desiredPaid) return;

      const byHandle = new Map();
      lines.forEach((l) => {
        const arr = byHandle.get(l.handle) || [];
        arr.push(l);
        byHandle.set(l.handle, arr);
      });

      const findLine = (handle, which) => {
        const arr = byHandle.get(handle) || [];
        if (which === 'free') return arr.find(x => x.isFreeLine);
        if (which === 'paid') return arr.find(x => x.isPaidLine);
        return null;
      };

      async function incLine(handle, which, delta, propsFallback) {
        const deltaQty = Number(delta) || 0;
        if (deltaQty <= 0) return;
        const line = findLine(handle, which);
        if (line && line.key) {
          await changeLineByKey(line.key, (Number(line.qty) || 0) + deltaQty);
          line.qty += deltaQty;
          return;
        }
        const mapNow = loadVariantMap();
        const ids = mapNow[handle];
        if (!ids?.freeId || !ids?.feeId) return;
        const variantId = which === 'paid' ? Number(ids.feeId) : Number(ids.freeId);
        const props = propsFallback || { _is_sample: '1', _sample_for_handle: handle };
        await addToCart(variantId, deltaQty, props);
      }

      async function decLine(line, delta) {
        const deltaQty = Number(delta) || 0;
        if (!line?.key || deltaQty <= 0) return;
        const next = Math.max(0, (Number(line.qty) || 0) - deltaQty);
        await changeLineByKey(line.key, next);
        line.qty = next;
      }

      if (paidQtyCurrent > desiredPaid) {
        let excess = paidQtyCurrent - desiredPaid;
        const paidLines = lines
          .filter(l => l.isPaidLine && l.qty > 0)
          .sort((a, b) => b.idx - a.idx);
        for (const pl of paidLines) {
          if (excess <= 0) break;
          const convertQty = Math.min(pl.qty, excess);
          await decLine(pl, convertQty);
          await incLine(pl.handle, 'free', convertQty, pl.props);
          excess -= convertQty;
        }
      } else {
        let need = desiredPaid - paidQtyCurrent;
        const freeLines = lines
          .filter(l => l.isFreeLine && l.qty > 0)
          .sort((a, b) => b.idx - a.idx);
        for (const fl of freeLines) {
          if (need <= 0) break;
          const convertQty = Math.min(fl.qty, need);
          await decLine(fl, convertQty);
          await incLine(fl.handle, 'paid', convertQty, fl.props);
          need -= convertQty;
        }
      }

      await refreshAllSampleButtons();
      notifyThemeCartUpdated();

    } catch (e) {
      console.warn('[samples] rebalance skipped:', e);
    } finally {
      rebalanceLock = false;
      if (rebalanceQueued) {
        rebalanceQueued = false;
        queueRebalance();
      }
    }
  }

  // ---------------- UI REFRESH ----------------
  async function refreshAllSampleButtons() {
    wireCalculatorProxyButtons();
    const buttons = document.querySelectorAll('[data-sample-button]');
    if (!buttons.length) return;
    buttons.forEach((btn) => {
      const handle = btn.dataset.productHandle || '';
      const ids = getIdsFromButton(btn);
      if (handle && ids?.freeId && ids?.feeId) upsertVariantMap(handle, ids);
    });
    try {
      const cart = await fetchCart();
      const count = countAllSamples(cart);
      buttons.forEach(btn => updateSampleButtonState(btn, cart, count));
    } catch (e) {}
  }

  // ---------------- CLICK HANDLER ----------------
  async function handleClick(e) {
    const btn = e.target.closest('[data-sample-button]');
    if (!btn) return;
    if (btn.hasAttribute('disabled')) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    try {
      const ids = getIdsFromButton(btn);
      if (!ids) throw new Error('Missing sample variant IDs on button');
      upsertVariantMap(btn.dataset.productHandle || '', ids);
      const cart = await fetchCart();

      // L1a GUARD: If a sample for this product is already in the cart,
      // refresh the button state and bail — don't add a duplicate.
      // Closes FOUC / bfcache / new-tab / concurrent-tab windows where the
      // button may still look enabled before disabled attr has been applied.
      const handle = btn.dataset.productHandle || '';
      if (handle && hasSampleForProduct(cart, handle)) {
        await refreshAllSampleButtons();
        return;
      }

      const count = countAllSamples(cart);
      const variantToAdd = count < FREE_LIMIT ? ids.freeId : ids.feeId;
      const props = {
        _is_sample: '1',
        _sample_for_handle: btn.dataset.productHandle || '',
        _sample_for_title: btn.dataset.productTitle || '',
        _sample_for_image: btn.dataset.productImage || '',
        _sample_for_url: btn.dataset.productUrl || '',
        _sample_free_variant_id: String(ids.freeId),
        _sample_fee_variant_id: String(ids.feeId)
      };
      await addToCart(variantToAdd, 1, props);
      await refreshAllSampleButtons();
      queueRebalance();
      notifyThemeCartUpdated();
    } catch (err) {
      console.error('[samples]', err);
      alert('Add failed: ' + (err?.message || err));
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  // ---------------- XHR FALLBACK (for apps using XHR not fetch) ----------------
  function installXhrFallback() {
    if (window.__reud_samples_xhr_wrapped__) return;
    window.__reud_samples_xhr_wrapped__ = true;
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.addEventListener('load', () => {
        try {
          if (url && /\/cart\/(add|change|update)\.js/.test(url)) {
            window.setTimeout(() => {
              refreshAllSampleButtons();
              queueRebalance();
            }, 60);
          }
        } catch (_) {}
      });
      return origOpen.call(this, method, url, ...rest);
    };
  }

  // ---------------- BOOT ----------------
  async function boot() {
    installXhrFallback();

    document.addEventListener('click', handleClick);

    await refreshAllSampleButtons();

    // Single set of event listeners — no duplicates
    [
      'cart:updated',
      'ajaxProduct:added',
      'ajaxProduct:removed',
      'ajaxCart:updated'
    ].forEach(ev => {
      document.addEventListener(ev, () => {
        refreshAllSampleButtons();
        queueRebalance();
      });
    });

    // cart:refresh triggers cart:updated (keep this chain)
    document.addEventListener('cart:refresh', () => {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    });

    // L1b GUARD: Browser back/forward cache — when the user hits Back and the
    // page is restored from bfcache, DOMContentLoaded doesn't fire so this
    // boot() doesn't re-run. Re-sync button state on pageshow so the button
    // reflects the current cart before any click can happen.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        refreshAllSampleButtons();
        queueRebalance();
      }
    });

    // L1c GUARD: Tab became visible again — cart may have changed in another
    // tab while this one was backgrounded. Refresh so button matches current
    // cart before any click.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        refreshAllSampleButtons();
        queueRebalance();
      }
    });

    // L1e GUARD: Block checkout while a sample rebalance is pending or
    // in-flight. Prevents the customer from paying for a muster that's
    // about to be converted back to free (e.g. after removing a muster
    // from a 6+ sample cart, one paid line converts to free — but the
    // cart still visually shows 1.50€ for a moment until the API calls
    // complete).
    //
    // Uses capture phase (true) so we run BEFORE Impulse / Shopify checkout
    // handlers. Only intercepts when rebalance is ACTUALLY pending — in the
    // normal case (99% of clicks) this is a single querySelector + a
    // no-op boolean check, zero visible cost.
    const CHECKOUT_SELECTOR = [
      'a[href="/checkout"]',
      'a[href^="/checkout?"]',
      'a[href*="/checkout?"]',
      'button[name="checkout"]',
      '.cart__submit',
      '[data-cart-submit]'
    ].join(', ');
    document.addEventListener('click', async function (e) {
      const btn = e.target.closest(CHECKOUT_SELECTOR);
      if (!btn) return;
      if (!isSampleRebalancePending()) return;

      // Fix #1: If this button is already being held by an earlier interceptor
      // invocation (aria-busy set), swallow the extra click but don't spin up
      // a parallel wait — the first invocation will re-fire the click when
      // rebalance completes. Prevents double-navigation from impatient clicks.
      if (btn.getAttribute('aria-busy') === 'true') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      // Rebalance still running — hold checkout back
      e.preventDefault();
      e.stopImmediatePropagation();

      const textEl = btn.querySelector('span') || btn;
      const originalText = textEl.textContent;
      const wasDisabled = btn.hasAttribute('disabled');
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-busy', 'true');
      // Fix #2: HTML spec doesn't honor `disabled` on <a> elements — set
      // pointer-events:none inline so the link is also visually/physically
      // unclickable during the wait, matching how <button disabled> behaves.
      btn.style.pointerEvents = 'none';
      textEl.textContent = 'Preise werden aktualisiert…';

      await waitForSampleRebalance();

      // Restore button appearance
      if (!wasDisabled) btn.removeAttribute('disabled');
      btn.removeAttribute('aria-busy');
      btn.style.pointerEvents = '';
      textEl.textContent = originalText;

      // Re-fire the click; isSampleRebalancePending() now returns false so
      // this pass-through reaches the actual checkout flow. For <a> elements
      // explicit navigation is a safer fallback than programmatic .click().
      if (btn.tagName === 'A' && btn.href) {
        window.location.href = btn.href;
      } else {
        btn.click();
      }
    }, true);

    window.setTimeout(() => {
      refreshAllSampleButtons();
      queueRebalance();
    }, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();